import { PageHeader } from '@/features/_components/ui/page-header'
import { useBreadcrumbs } from '@/features/_hooks/use-breadcrumbs'
import { queries } from '@/features/_lib/react-query'
import type { TExposedApplicationAggregateDTO } from '@doutorsim/shared/applications'
import type { TExposedCompanyDTO } from '@doutorsim/shared/companies'
import type { TExposedPersonAggregateDTO } from '@doutorsim/shared/exposed/persons'
import type { TCreateUserOutputDTO } from '@doutorsim/shared/users'
import { useQuery } from '@tanstack/react-query'
import { useLocation } from '@tanstack/react-router'

interface GuarantorHeaderProps {
  application: TExposedApplicationAggregateDTO
  company: TExposedCompanyDTO
  person: TExposedPersonAggregateDTO
  guarantor: TExposedPersonAggregateDTO
  userType: TCreateUserOutputDTO['userType']
}

export const GuarantorHeader = ({
  application,
  company,
  person,
  guarantor,
  userType,
}: GuarantorHeaderProps) => {
  const { pathname } = useLocation()

  const { data: facility } = useQuery(
    queries.companies.facility(application.companyId, application.facilityId),
  )

  const isStaffRoute = pathname.startsWith('/applications/')
  const isFacilityRoute = pathname.includes('/facilities/')
  const searchSuffix = isStaffRoute
    ? `?companyId=${application.companyId}&facilityId=${application.facilityId}`
    : ''

  const facilitySlug = facility?.slug ?? application.facilityId

  const base = isStaffRoute
    ? `/applications/${application.id}/guarantors/${guarantor.id}`
    : isFacilityRoute
      ? `/companies/${company.slug}/facilities/${facilitySlug}/applications/${application.id}/guarantors/${guarantor.id}`
      : `/companies/${company.slug}/applications/${application.id}/guarantors/${guarantor.id}`

  const facilityPersonsHref = `/companies/${company.slug}/facilities/${facilitySlug}/applications`
  const personGuarantorsHref = isStaffRoute
    ? `/applications/${application.id}/guarantors${searchSuffix}`
    : isFacilityRoute
      ? `/companies/${company.slug}/facilities/${facilitySlug}/applications/${application.id}/guarantors`
      : `/companies/${company.slug}/applications/${application.id}/guarantors`

  useBreadcrumbs(
    facility
      ? isStaffRoute
        ? [
            { label: 'Pacientes', href: '/applications' },
            { label: person.name, href: personGuarantorsHref },
            { label: guarantor.name },
          ]
        : isFacilityRoute
          ? userType === 'staff'
            ? [
                { label: 'Empresas', href: '/companies' },
                {
                  label: company.name,
                  href: `/companies/${company.slug}/facilities`,
                },
                { label: facility.name, href: facilityPersonsHref },
                { label: person.name, href: personGuarantorsHref },
                { label: guarantor.name },
              ]
            : [
                {
                  label: 'Sua Instituição',
                  href: `/companies/${company.slug}/facilities`,
                },
                { label: facility.name, href: facilityPersonsHref },
                { label: person.name, href: personGuarantorsHref },
                { label: guarantor.name },
              ]
          : userType === 'staff'
            ? [
                { label: 'Empresas', href: '/companies' },
                {
                  label: company.name,
                  href: `/companies/${company.slug}/applications`,
                },
                { label: person.name, href: personGuarantorsHref },
                { label: guarantor.name },
              ]
            : [
                {
                  label: 'Pacientes',
                  href: `/companies/${company.slug}/applications`,
                },
                { label: person.name, href: personGuarantorsHref },
                { label: guarantor.name },
              ]
      : [],
  )

  if (!application || !guarantor || !person || !company) return null

  return (
    <PageHeader
      title={guarantor.name}
      subtitle={
        <span className="text-sm text-muted-foreground">
          Avalista de{' '}
          <span className="font-semibold text-brand-green-primary-5">{person.name}</span>
        </span>
      }
      backHref={personGuarantorsHref}
      tabs={[
        {
          label: 'Perfil',
          href: `${base}/profile${searchSuffix}`,
          isActive: true,
        },
      ]}
    />
  )
}
